import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FaUser } from 'react-icons/fa';
import { FaUserGroup } from 'react-icons/fa6';
import { GoClock } from 'react-icons/go';
import { GrServices } from 'react-icons/gr';
import { IoMdCheckboxOutline } from 'react-icons/io';
import { IoMegaphone, IoVolumeMuteOutline } from 'react-icons/io5';

import { imageVariantLink } from '@/common/media';
import { ActionsButton, type ActionMenuItem } from '@/components/ActionsButton';
import { Avatar } from '@/components/Avatar';
import { ButtonOutlinedSmall, ButtonSmall } from '@/components/Button';
import { TextArea } from '@/components/Inputs';
import { TemporaryBadge } from '@/components/TemporaryBadge/TemporaryBadge';
import { usePersonsMap } from '@/hooks/usePersonsMap';
import type { MockMessageProps } from '@/mockData/mockData.tsx';
import { useAuth } from '@/store/auth/auth.store';
import { useChats } from '@/store/chats/chats.store';

import styles from './TaskCard.module.scss';

type MessageProps = Omit<MockMessageProps, 'id'> & {
  unread?: boolean;
  avatar?: string | null;
  isMuted?: boolean;
  isChat?: boolean;
  room?: any;
  createdAt?: string;
  updatedAt?: string;
  creatorId?: string;
  messageId?: string;
  roomId?: string;
  onDeleteSuccess?: () => void;
  actionsButton?: React.ReactNode;
};

const Message = ({
  sender,
  message,
  unread = false,
  avatar,
  isMuted,
  isChat,
  room,
  createdAt,
  updatedAt,
  creatorId,
  messageId,
  roomId,
  onDeleteSuccess,
  actionsButton,
}: MessageProps) => {
  const personsMap = usePersonsMap();
  const person = creatorId ? personsMap.get(creatorId) : null;
  const currentUserId = useAuth((state) => state.session?.id);
  const deleteMessage = useChats((state) => state.deleteMessage);
  const editMessage = useChats((state) => state.editMessage);

  const [isEditing, setIsEditing] = useState(false);
  const [editedText, setEditedText] = useState(message);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  // Use person data if available, otherwise fall back to sender prop
  const firstName = person?.person_info?.first_name || sender?.split(' ')[0];
  const lastName = person?.person_info?.last_name || sender?.split(' ')[1];
  const avatarSrc = imageVariantLink(person?.avatar, 40) || avatar || undefined;
  const displayName = person
    ? `${person.person_info?.first_name} ${person.person_info?.last_name}`
    : sender;

  const roomTypeIcon = useMemo<ReactNode | null>(() => {
    const type = room?.type;
    const topicCode = room?.topic_code ?? '';
    if (type === 'one_to_one') return <FaUser />;
    if (type === 'chat') return <FaUserGroup />;
    if (type === 'broadcast') return <IoMegaphone />;
    if (type === 'bid') return <GrServices />;
    if (type === 'comments') {
      if (topicCode.includes('event')) return <GoClock />;
      return <IoMdCheckboxOutline />;
    }
    return null;
  }, [room?.type, room?.topic_code]);

  // Show actions only for comments (when messageId and roomId are provided) and user is the creator
  const showActions = !isChat && messageId && roomId && creatorId && currentUserId === creatorId;

  const handleEdit = useCallback(() => {
    setIsEditing(true);
    setEditedText(message);
  }, [message]);

  // Move cursor to the end when editing starts
  useEffect(() => {
    if (isEditing && textAreaRef.current) {
      const textarea = textAreaRef.current;
      const length = textarea.value.length;
      textarea.setSelectionRange(length, length);
    }
  }, [isEditing]);

  const handleCancelEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(false);
    setEditedText(message);
  };

  const handleSaveEdit = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!messageId || !roomId || editedText.trim() === '') return;

    setIsSubmitting(true);
    try {
      await editMessage(roomId, { id: messageId, text: editedText });
      setIsEditing(false);
      onDeleteSuccess?.(); // Trigger refresh to show updated message
    } catch (error) {
      console.error('Failed to edit message:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const actionItems = useMemo<ActionMenuItem[]>(() => {
    if (!showActions) return [];

    const handleDelete = async () => {
      if (!messageId || !roomId) return;
      try {
        await deleteMessage(roomId, messageId);
        onDeleteSuccess?.();
      } catch (error) {
        console.error('Failed to delete message:', error);
      }
    };

    return [
      {
        label: 'Edit',
        onClick: handleEdit,
      },
      {
        label: 'Delete',
        onClick: handleDelete,
        variant: 'danger' as const,
      },
    ];
  }, [showActions, messageId, roomId, deleteMessage, onDeleteSuccess, handleEdit]);

  return (
    <div className={styles.message}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
        <div style={{ position: 'relative' }}>
          <Avatar
            src={avatarSrc}
            firstName={firstName}
            lastName={lastName}
            size={40}
            className={styles.messageAvatar}
          />
          {roomTypeIcon && <span className={styles.roomTypeIcon}>{roomTypeIcon}</span>}
        </div>
        <div className={styles.messageContainer}>
          <h4 className={styles.messageSender}>
            <div className={styles.messageSenderRow}>
              <span className={styles.messageName}>
                <span className={styles.messageNameText}>
                  {displayName.split('- chat room')[0]}
                </span>
                {room?.type === 'temporary' && <TemporaryBadge />}
                {room?.type !== 'broadcast' && isChat && isMuted && (
                  <span className={styles.muteIcon}>
                    <IoVolumeMuteOutline />
                  </span>
                )}
              </span>
            </div>
            {updatedAt && (
              <span className={styles.messageDate}>
                {new Date(updatedAt).toLocaleDateString('en-GB', {
                  day: '2-digit',
                  month: 'short',
                })}{' '}
                {new Date(updatedAt).toLocaleTimeString('en-GB', {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
                {createdAt &&
                  updatedAt &&
                  new Date(updatedAt).getTime() - new Date(createdAt).getTime() > 1000 &&
                  ' (edited)'}
              </span>
            )}
            {/*<span className={styles.messageRole}>{role}</span>*/}
          </h4>
          {isEditing ? (
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}
              onClick={(e) => e.stopPropagation()}
            >
              <TextArea
                ref={textAreaRef}
                value={editedText}
                onChange={(e) => setEditedText(e.target.value)}
                rows={3}
                disabled={isSubmitting}
                autoFocus
              />
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <ButtonOutlinedSmall onClick={handleCancelEdit} disabled={isSubmitting}>
                  Cancel
                </ButtonOutlinedSmall>
                <ButtonSmall
                  onClick={handleSaveEdit}
                  disabled={isSubmitting || editedText.trim() === ''}
                >
                  {isSubmitting ? 'Saving…' : 'Save'}
                </ButtonSmall>
              </div>
            </div>
          ) : (
            <p className={styles.messageText}>
              {message.length > 50 ? `${message.slice(0, 100)}` : message}
            </p>
          )}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        {actionsButton}
        {showActions && !isEditing && (
          <ActionsButton items={actionItems} ariaLabel="Message Actions" />
        )}
      </div>
      {unread ? <span className={styles.messageDot} /> : null}
    </div>
  );
};

export default Message;
